// @ts-nocheck
/**
 * "Insights" tab body for the FinalMappingCard — the PE-analyst "first look".
 *
 * On mount it does a read-only GET /agent/sessions/{id}/insights. If none has
 * been generated yet (404 → null) it POSTs the same endpoint automatically to
 * run the single-shot analyst pass (~20-60s) — no button, same click-the-tab
 * contract as RatiosTab. The in-flight POST is registered in generationJobs,
 * so clicking away unmounts the view but the request finishes in the
 * background (the server persists the result); revisiting the tab re-attaches
 * to the running promise or picks the finished result up from the server
 * cache. A cached schema-v1 payload (the old prose layout) is silently
 * regenerated once with force so old sessions land on the new format.
 *
 * Renders the v2 "carousel" shape: a fixed header (verdict chip + one-breath
 * headline + a muted stats strip), then one insight per slide — hook, up to
 * two facts, the labeled best guess, and the single management question —
 * stepped with arrows / dots / keyboard / swipe. The last slide collects the
 * blind spots (what the statements can't show) as a copyable data-request
 * list. Every slide copies as a self-contained plain-text block.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  EyeOff,
  Loader2,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';

import { generateAgentInsights, getAgentInsights } from '../../utils/agentApi';
import { getInflightJob, getJobError, startJob } from '../../utils/generationJobs';
import './InsightsTab.css';

const SCHEMA_VERSION = 2;

const CALL_META = {
  dig_in: { label: 'Dig in', icon: CheckCircle2 },
  watch_risks: { label: 'Look deeper, watch risks', icon: ShieldAlert },
  walk_away: { label: 'Likely walk away', icon: XCircle },
};

const CATEGORY_LABELS = {
  earnings: 'Earnings',
  cash: 'Cash',
  growth: 'Growth',
  concentration: 'Concentration',
  working_capital: 'Working capital',
  data_quality: 'Data quality',
};

const BLIND_SPOT_LABELS = {
  customer_concentration: 'Customer concentration',
  supplier_concentration: 'Supplier concentration',
  revenue_retention: 'Revenue retention',
  key_man: 'Key-man risk',
  cash_conversion: 'Cash conversion',
  revenue_channels: 'Revenue channels',
};

const SCALE_BASIS_LABELS = {
  trailing_year: 'trailing year',
  latest_year: 'latest full year',
};

const fmtCompactMoney = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(0)}k`;
  return `${sign}$${a.toFixed(0)}`;
};

const insightCopyText = (ins) => {
  const lines = [ins.hook];
  (ins.facts || []).forEach((f) => lines.push(`- ${f}`));
  if (ins.best_guess) lines.push(`Best guess: ${ins.best_guess}`);
  if (ins.question) lines.push(`Ask: ${ins.question}`);
  return lines.join('\n');
};

const blindSpotsCopyText = (spots) =>
  [
    'Data requests:',
    ...spots.map(
      (b, i) =>
        `${i + 1}. ${b.data_request}${
          BLIND_SPOT_LABELS[b.key] ? ` (${BLIND_SPOT_LABELS[b.key].toLowerCase()})` : ''
        }`,
    ),
  ].join('\n');

const InsightsTab = ({ sessionId }) => {
  // loading (initial GET) | generating | ready | error
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [slide, setSlide] = useState(0);
  // null | 'slide' | 'all' — which copy button just fired, for its feedback.
  const [copied, setCopied] = useState(null);
  // A v1 payload triggers exactly one silent force-regenerate; if the server
  // still answers v1 (old deploy), fall through to a manual re-run state
  // instead of looping the LLM.
  const autoRegenRef = useRef(false);
  const copiedTimer = useRef(null);
  const swipeX = useRef(null);

  const jobKey = `insights:${sessionId}`;

  const land = (d) => {
    setData(d);
    setSlide(0);
    setStatus('ready');
  };

  // Any resolved payload flows through here so the v1 auto-regen guard is
  // applied uniformly (initial GET, re-attached job, fresh POST).
  const resolve = (d) => {
    if (d && d.schema_version !== SCHEMA_VERSION && !autoRegenRef.current) {
      autoRegenRef.current = true;
      attach(startJob(jobKey, () => generateAgentInsights(sessionId, { force: true })));
      return;
    }
    land(d);
  };

  const attach = (job) => {
    setStatus('generating');
    setError(null);
    job
      .then((d) => resolve(d))
      .catch((err) => {
        setError(err.message);
        setStatus('error');
      });
  };

  useEffect(() => {
    let cancelled = false;
    const guard =
      (fn) =>
      (...args) => {
        if (!cancelled) fn(...args);
      };

    // A generation kicked off on an earlier visit may still be running —
    // re-attach instead of re-firing.
    const running = getInflightJob(jobKey);
    if (running) {
      setStatus('generating');
      running.then(guard(resolve)).catch(
        guard((err) => {
          setError(err.message);
          setStatus('error');
        }),
      );
      return () => {
        cancelled = true;
      };
    }
    // A generation failed while the tab was closed: show the error, don't
    // silently re-fire the LLM pass. Try again clears it.
    const failed = getJobError(jobKey);
    if (failed) {
      setError(failed);
      setStatus('error');
      return () => {
        cancelled = true;
      };
    }

    setStatus('loading');
    setError(null);
    getAgentInsights(sessionId)
      .then(
        guard((d) => {
          if (d) {
            resolve(d);
          } else {
            // Nothing cached server-side: generation starts the moment the
            // tab opens. The job outlives this component (see generationJobs).
            attach(startJob(jobKey, () => generateAgentInsights(sessionId, { force: false })));
          }
        }),
      )
      .catch(
        guard((err) => {
          setError(err.message);
          setStatus('error');
        }),
      );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const run = (force) => {
    setStatus('generating');
    setError(null);
    attach(startJob(jobKey, () => generateAgentInsights(sessionId, { force })));
  };

  const insights = useMemo(() => {
    const list = (data && data.insights) || [];
    return [...list].sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, [data]);
  const blindSpots = (data && data.blind_spots) || [];
  const slideCount = insights.length + (blindSpots.length ? 1 : 0);
  const onBlindSpots = blindSpots.length > 0 && slide === insights.length;

  const goTo = (i) => {
    setSlide(Math.max(0, Math.min(slideCount - 1, i)));
    setCopied(null);
  };

  const copyText = (text, which) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(which);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(null), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };

  const copySlide = () =>
    copyText(
      onBlindSpots
        ? blindSpotsCopyText(blindSpots)
        : insightCopyText(insights[slide] || {}),
      'slide',
    );

  const copyAll = (callLabel, headline, scaleLine) => {
    const lines = [callLabel];
    if (headline) lines.push(headline);
    if (scaleLine) lines.push(scaleLine);
    insights.forEach((ins, i) => {
      const tag = `${CATEGORY_LABELS[ins.category] || ins.category} · ${
        ins.severity
      }`.toUpperCase();
      lines.push('', `${i + 1}. [${tag}] ${ins.hook}`);
      (ins.facts || []).forEach((f) => lines.push(`- ${f}`));
      if (ins.best_guess) lines.push(`Best guess: ${ins.best_guess}`);
      if (ins.question) lines.push(`Ask: ${ins.question}`);
    });
    if (blindSpots.length) lines.push('', blindSpotsCopyText(blindSpots));
    copyText(lines.join('\n'), 'all');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(slide - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(slide + 1);
    }
  };
  const onPointerDown = (e) => {
    swipeX.current = e.clientX;
  };
  const onPointerUp = (e) => {
    if (swipeX.current == null) return;
    const dx = e.clientX - swipeX.current;
    swipeX.current = null;
    if (Math.abs(dx) < 40) return;
    goTo(dx < 0 ? slide + 1 : slide - 1);
  };

  if (status === 'loading') {
    return (
      <div className="it-state">
        <Loader2 size={16} className="it-spin" />
        <span>Loading…</span>
      </div>
    );
  }

  if (status === 'generating') {
    return (
      <div className="it-state">
        <Loader2 size={18} className="it-spin" />
        <span>Reviewing the statements like an analyst. This takes a moment.</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="it-state it-state-error">
        <AlertTriangle size={16} />
        <span>{error || 'Could not generate insights.'}</span>
        <button type="button" className="it-btn" onClick={() => run(false)}>
          <RefreshCw size={13} /> Try again
        </button>
      </div>
    );
  }

  // status === 'ready'
  const v = (data && data.verdict) || {};
  const call = CALL_META[v.call] || CALL_META.watch_risks;
  const CallIcon = call.icon;

  // The one-shot regen guard failed to land a v2 payload (old server build):
  // offer a manual re-run rather than rendering fields that no longer exist.
  if (data && data.schema_version !== SCHEMA_VERSION) {
    return (
      <div className="it-state">
        <span>This insight uses an older format.</span>
        <button type="button" className="it-btn" onClick={() => run(true)}>
          <RefreshCw size={13} /> Regenerate
        </button>
      </div>
    );
  }

  const scale = (data && data.scale) || null;
  const scaleMoney = scale ? fmtCompactMoney(scale.ltm_ebitda) : null;
  const stripParts = [];
  if (scaleMoney) stripParts.push(`Adj EBITDA ${scaleMoney}`);
  if (scale && scale.band_label) stripParts.push(scale.band_label);
  stripParts.push(`${insights.length} insight${insights.length === 1 ? '' : 's'}`);

  const current = onBlindSpots ? null : insights[slide];
  const SlideCopyIcon = copied === 'slide' ? Check : Copy;
  const AllCopyIcon = copied === 'all' ? Check : Copy;

  return (
    <div className="it-wrap">
      <div className="it-verdict">
        <div className="it-verdict-call">
          <CallIcon size={16} />
          <span className="it-verdict-label">{call.label}</span>
        </div>
        {v.headline && <div className="it-verdict-headline">{v.headline}</div>}
        <div
          className="it-strip"
          title={
            scale && scale.basis
              ? `Adjusted EBITDA on a ${SCALE_BASIS_LABELS[scale.basis] || scale.basis} basis`
              : undefined
          }
        >
          {stripParts.join(' · ')}
        </div>
      </div>

      {slideCount > 0 && (
        <div
          className="it-carousel"
          role="group"
          aria-roledescription="carousel"
          aria-label="Insights"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
        >
          <div className={`it-slide ${current ? `sev-${current.severity}` : 'is-blind'}`}>
            <div className="it-slide-top">
              {current ? (
                <span className={`it-tag sev-${current.severity}`}>
                  {CATEGORY_LABELS[current.category] || current.category}
                  <span className="it-tag-dot">·</span>
                  {current.severity}
                </span>
              ) : (
                <span className="it-tag is-blind">
                  <EyeOff size={11} /> Blind spots
                </span>
              )}
              <button
                type="button"
                className={`it-copy-slide ${copied === 'slide' ? 'is-copied' : ''}`}
                onClick={copySlide}
                title={onBlindSpots ? 'Copy data-request list' : 'Copy insight'}
                aria-label={onBlindSpots ? 'Copy data-request list' : 'Copy insight'}
              >
                <SlideCopyIcon size={13} />
                {copied === 'slide' ? 'Copied' : 'Copy'}
              </button>
            </div>

            {current ? (
              <>
                <div className="it-hook">{current.hook}</div>
                {(current.facts || []).length > 0 && (
                  <ul className="it-facts">
                    {current.facts.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                )}
                {current.best_guess && (
                  <p className="it-guess-line">
                    <span className="it-line-tag">Best guess</span>
                    {current.best_guess}
                  </p>
                )}
                {current.question && (
                  <p className="it-ask-line">
                    <span className="it-line-tag">Ask</span>
                    {current.question}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="it-hook">What the statements can't show</div>
                <ul className="it-blind-list">
                  {blindSpots.map((b, i) => (
                    <li key={i}>
                      <span className="it-blind-key">
                        {BLIND_SPOT_LABELS[b.key] || b.key}
                      </span>
                      <span className="it-blind-req">{b.data_request}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="it-controls">
            <button
              type="button"
              className="it-nav"
              onClick={() => goTo(slide - 1)}
              disabled={slide === 0}
              aria-label="Previous insight"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="it-dots" role="tablist">
              {insights.map((ins, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={slide === i}
                  aria-label={`Insight ${i + 1}`}
                  className={`it-dot sev-${ins.severity} ${slide === i ? 'is-active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
              {blindSpots.length > 0 && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={onBlindSpots}
                  aria-label="Blind spots"
                  className={`it-dot is-blind ${onBlindSpots ? 'is-active' : ''}`}
                  onClick={() => goTo(insights.length)}
                />
              )}
            </div>
            <span className="it-counter">
              {slide + 1} / {slideCount}
            </span>
            <button
              type="button"
              className="it-nav"
              onClick={() => goTo(slide + 1)}
              disabled={slide === slideCount - 1}
              aria-label="Next insight"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="it-foot">
        <span className="it-disclaimer">A first-look filter, not a valuation.</span>
        <span className="it-foot-actions">
          <button
            type="button"
            className={`it-btn it-btn-ghost ${copied === 'all' ? 'is-copied' : ''}`}
            onClick={() =>
              copyAll(call.label, v.headline, stripParts.slice(0, -1).join(' · '))
            }
            title="Copy the whole assessment as text"
          >
            <AllCopyIcon size={12} /> {copied === 'all' ? 'Copied' : 'Copy all'}
          </button>
          <button type="button" className="it-btn it-btn-ghost" onClick={() => run(true)}>
            <RefreshCw size={12} /> Re-run
          </button>
        </span>
      </div>
    </div>
  );
};

export default InsightsTab;
